import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, CircleHelp, Download, Plus, Printer, Search, Upload } from 'lucide-react'
import { api } from '../../api/client'
import { saveArea } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { ScanButton } from '../../components/BarcodeScanner'
import Modal from '../../components/Modal'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import BinNav from './BinNav'
import LabelSheet, { Label } from './LabelSheet'
import { useLocations } from './inventoryShared'
import { printPage, useAutoPrint } from '../../utils/print'

const LEVELS = ['Department', 'Aisle', 'Section', 'Shelf', 'Bin']
// Each level sits inside the one before it.
const PARENT_LEVEL = { Department: null, Aisle: 'Department', Section: 'Aisle', Shelf: 'Section', Bin: 'Shelf' }
const CHILD_LEVEL = { Department: 'Aisle', Aisle: 'Section', Section: 'Shelf', Shelf: 'Bin', Bin: null }
// What each level is, with example codes; the first code is suggested for a new parent.
const LEVEL_HELP = {
  Department: { what: 'A big area of the shop or store room.', eg: 'DRY for Dry goods, COLD for the cold room', first: '' },
  Aisle: { what: 'A row or walkway inside a department.', eg: 'A1, A2', first: 'A1' },
  Section: { what: 'A stretch of the aisle, such as one rack or bay.', eg: 'S1, S2', first: 'S1' },
  Shelf: { what: 'One shelf on that rack.', eg: 'SH1 to SH4', first: 'SH1' },
  Bin: { what: 'The exact spot items are kept: a box, tray or space on the shelf. Stock is put away into bins.', eg: 'B1, B2', first: 'B1' },
}
/** B1 -> B2, SH09 -> SH10; '' when the code doesn't end in a number. */
const nextCode = (code) => {
  const m = String(code || '').match(/^(.*?)(\d+)$/)
  return m ? `${m[1]}${String(Number(m[2]) + 1).padStart(m[2].length, '0')}` : ''
}
const CSV_COLUMNS = ['area_id', 'location_id', 'parent_id', 'level', 'code', 'name', 'path', 'barcode', 'is_active']
const blank = (locationId = '') => ({ area_id: '', location_id: locationId, parent_id: '', level: 'Bin', code: '', name: '', barcode: '', is_active: '1' })
const isActive = (a) => Number(a?.is_active ?? 1) !== 0

// Departments, aisles, sections, shelves and bins in each location, as a
// tree. Add or edit one at a time, or import a CSV.
export default function StorageAreas() {
  const { notify } = useToast()
  const locations = useLocations()
  const fileRef = useRef(null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [locFilter, setLocFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [formError, setFormError] = useState('')
  const [chain, setChain] = useState('') // "Shelf SH1 saved. Now add a bin…"
  const [showGuide, setShowGuide] = useState(false)
  const [labels, setLabels] = useState(null) // { rootId, levels: [...], size }
  const [printing, setPrinting] = useState(null) // { list, size } on the printed sheet
  const [expanded, setExpanded] = useState(() => new Set()) // open departments (top-level ids); closed by default

  const refresh = async () => {
    try { setRows((await api.get('/inventory/areas')).data || []) } catch (e) { setError(e.message || 'Could not load storage areas.') }
    setLoading(false)
  }
  useEffect(() => { refresh() }, [])

  // Depth-first order per location so children sit under their parent.
  const tree = useMemo(() => {
    const term = search.trim().toLowerCase()
    const byParent = new Map()
    rows.forEach((r) => {
      const key = `${r.location_id}:${r.parent_id || 0}`
      if (!byParent.has(key)) byParent.set(key, [])
      byParent.get(key).push(r)
    })
    // Each row remembers its top-level area (its department), so a closed
    // department hides everything inside it.
    const out = []
    const walk = (locId, parentId, depth, top) => {
      const kids = (byParent.get(`${locId}:${parentId || 0}`) || []).sort((a, b) => String(a.code).localeCompare(String(b.code), undefined, { numeric: true }))
      kids.forEach((k) => { const t = top ?? k.area_id; out.push({ ...k, depth, top: t }); walk(locId, k.area_id, depth + 1, t) })
    }
    const locIds = [...new Set(rows.map((r) => r.location_id))]
    locIds.forEach((id) => walk(id, 0, 0, null))
    // Areas whose parent is missing still show, at the top level.
    const seen = new Set(out.map((r) => r.area_id))
    rows.filter((r) => !seen.has(r.area_id)).forEach((r) => out.push({ ...r, depth: 0, top: r.area_id }))
    return out.filter((r) => (locFilter === 'all' || String(r.location_id) === locFilter)
      && (!term || [r.name, r.code, r.path, r.barcode].join(' ').toLowerCase().includes(term)))
  }, [rows, locFilter, search])

  // What each department holds, for its collapsed row.
  const inside = useMemo(() => {
    const m = new Map()
    tree.forEach((r) => {
      if (r.depth === 0) return
      const c = m.get(r.top) || { areas: 0, bins: 0 }
      c.areas += 1
      if (r.level === 'Bin') c.bins += 1
      m.set(r.top, c)
    })
    return m
  }, [tree])
  const searching = search.trim() !== ''
  const shownRows = searching ? tree : tree.filter((r) => r.depth === 0 || expanded.has(r.top))
  const tops = tree.filter((r) => r.depth === 0 && inside.has(r.top))
  const toggle = (id) => setExpanded((cur) => { const next = new Set(cur); if (next.has(id)) next.delete(id); else next.add(id); return next })
  /** The top-level area above an area (itself when it is top level). */
  const topOf = (id) => {
    const byId = new Map(rows.map((r) => [r.area_id, r]))
    let a = byId.get(Number(id))
    while (a?.parent_id && byId.has(a.parent_id)) a = byId.get(a.parent_id)
    return a?.area_id ?? Number(id)
  }

  const locName = (id) => locations?.find((l) => String(l.location_id) === String(id))?.name || ''
  const parentOptions = form ? rows.filter((r) => String(r.location_id) === String(form.location_id) && r.level === PARENT_LEVEL[form.level]) : []
  const parent = form && rows.find((r) => String(r.area_id) === String(form.parent_id))
  const code = String(form?.code || '').trim()
  const path = form && code ? (PARENT_LEVEL[form.level] ? (parent ? `${parent.path}/${code}` : '') : `/${code}`).replace(/\/+/g, '/') : ''

  // New areas start as a department until there is somewhere to put a bin.
  const openNew = (level) => {
    setFormError('')
    setChain('')
    const loc = locFilter !== 'all' ? locFilter : String(locations?.[0]?.location_id || '')
    const start = level || (rows.some((r) => r.level === 'Shelf' && String(r.location_id) === loc) ? 'Bin' : 'Department')
    setForm({ ...blank(loc), level: start })
  }
  const openEdit = (r) => { setFormError(''); setChain(''); setForm({ ...blank(), ...r, location_id: String(r.location_id), parent_id: r.parent_id ? String(r.parent_id) : '', is_active: isActive(r) ? '1' : '0' }) }
  const closeForm = () => { setForm(null); setChain('') }
  const set = (patch) => { setFormError(''); setForm((f) => ({ ...f, ...patch })) }

  // Labels: one area and everything inside it, or every area at the chosen
  // location, limited to the ticked levels, in path order.
  const labelList = useMemo(() => {
    if (!labels) return []
    const kids = new Map()
    rows.forEach((r) => { const k = String(r.parent_id || 0); if (!kids.has(k)) kids.set(k, []); kids.get(k).push(r) })
    let pool
    if (labels.rootId) {
      pool = []
      const walk = (id) => (kids.get(String(id)) || []).forEach((c) => { pool.push(c); walk(c.area_id) })
      const root = rows.find((r) => r.area_id === labels.rootId)
      if (root) { pool.push(root); walk(root.area_id) }
    } else {
      pool = rows.filter((r) => locFilter === 'all' || String(r.location_id) === locFilter)
    }
    return pool
      .filter((r) => isActive(r) && labels.levels.includes(r.level))
      .map((r) => ({ ...r, location_name: r.location_name || locName(r.location_id) }))
      .sort((a, b) => String(a.location_name).localeCompare(String(b.location_name)) || String(a.path).localeCompare(String(b.path), undefined, { numeric: true }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labels, rows, locFilter, locations])
  const labelRoot = labels?.rootId ? rows.find((r) => r.area_id === labels.rootId) : null
  const openLabels = (root) => setLabels({ rootId: root?.area_id || null, levels: root?.level === 'Bin' ? ['Bin'] : ['Shelf', 'Bin'], size: 'small' })
  const toggleLevel = (level) => setLabels((l) => ({ ...l, levels: l.levels.includes(level) ? l.levels.filter((x) => x !== level) : [...l.levels, level] }))

  const printLabels = () => {
    setPrinting({ list: labelList, size: labels.size })
    setLabels(null)
    // Give the sheet a moment to render, then print it on its own.
    setTimeout(() => {
      const cleanup = () => document.body.classList.remove('ci-printing')
      document.body.classList.add('ci-printing')
      window.addEventListener('afterprint', cleanup, { once: true })
      window.print()
      setTimeout(cleanup, 1500)
    }, 100)
  }

  // From the phone app: the same labels, chosen there, printed here.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get('print') !== '1' || !p.get('label_levels')) return
    if (p.get('label_location')) setLocFilter(p.get('label_location'))
    setLabels({ rootId: p.get('label_root') ? Number(p.get('label_root')) : null, levels: p.get('label_levels').split(','), size: p.get('label_size') === 'large' ? 'large' : 'small' })
  }, [])
  useAutoPrint(!loading && !!labels, printLabels)
  const printHere = () => printPage(printLabels, {
    label_root: labels.rootId || '', label_levels: labels.levels.join(','), label_size: labels.size, label_location: locFilter,
  })

  const save = async () => {
    setFormError('')
    if (!form.location_id) { setFormError('Choose the location.'); return }
    if (!String(form.code).trim()) { setFormError('Enter a short code, e.g. A1 or B3.'); return }
    if (PARENT_LEVEL[form.level] && !form.parent_id) { setFormError(`Choose the ${PARENT_LEVEL[form.level].toLowerCase()} it’s in.`); return }
    setSaving(true)
    try {
      const code = String(form.code).trim()
      const res = await saveArea({
        area_id: form.area_id ? Number(form.area_id) : undefined,
        location_id: Number(form.location_id),
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        level: form.level,
        code: String(form.code).trim(),
        name: String(form.name || '').trim() || undefined,
        path: path || undefined,
        barcode: String(form.barcode || '').trim() || undefined,
        is_active: form.is_active,
      })
      await refresh()
      if (form.area_id) {
        notify('Area saved')
        closeForm()
        return
      }
      // Keep going: the next level inside what was just added, or the next
      // bin on the same shelf. Its department opens so the new rows show.
      const top = form.parent_id ? topOf(form.parent_id) : res?.area_id
      if (top) setExpanded((cur) => new Set(cur).add(Number(top)))
      const child = CHILD_LEVEL[form.level]
      if (child && res?.area_id) {
        setChain(`${form.level} ${code} saved. Now add ${child === 'Aisle' ? 'an' : 'a'} ${child.toLowerCase()} inside it, or press Done.`)
        setForm({ ...blank(form.location_id), level: child, parent_id: String(res.area_id), code: LEVEL_HELP[child].first })
      } else {
        setChain(`Bin ${code} saved. Add the next bin on this shelf, or press Done.`)
        setForm({ ...blank(form.location_id), level: 'Bin', parent_id: form.parent_id, code: nextCode(code) })
      }
    } catch (e) {
      setFormError(e.message || 'Could not save this area.')
    } finally {
      setSaving(false)
    }
  }

  const importCsv = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const lines = (await file.text()).split(/\r?\n/).filter(Boolean)
    if (lines.length < 2) { notify('That file has no rows.'); return }
    const head = lines.shift().split(',').map((h) => h.trim())
    let imported = 0
    let skipped = 0
    for (const line of lines) {
      const cells = line.split(',')
      const r = Object.fromEntries(head.map((h, i) => [h, (cells[i] ?? '').trim()]))
      const locId = Number(r.location_id)
      if (!(locId > 0) || !LEVELS.includes(r.level) || (r.area_id && !Number.isFinite(Number(r.area_id))) || (r.parent_id && !Number.isFinite(Number(r.parent_id)))) { skipped += 1; continue }
      const flag = String(r.is_active ?? '1').toLowerCase()
      try {
        await saveArea({
          area_id: r.area_id ? Number(r.area_id) : undefined,
          location_id: locId,
          parent_id: r.parent_id ? Number(r.parent_id) : null,
          level: r.level,
          code: r.code || undefined,
          name: r.name || undefined,
          path: r.path || undefined,
          barcode: r.barcode || undefined,
          is_active: ['0', 'false', 'no', 'n'].includes(flag) ? '0' : '1',
        })
        imported += 1
      } catch {
        skipped += 1
      }
    }
    notify(`Imported ${imported} ${imported === 1 ? 'area' : 'areas'}${skipped ? `; skipped ${skipped}` : ''}`)
    await refresh()
  }

  const exportCsv = () => {
    const csv = [CSV_COLUMNS.join(','), ...rows.map((r) => CSV_COLUMNS.map((c) => r[c] ?? '').join(','))].join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = 'storage_areas.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="ci-page">
      <BinNav
        title="Storage areas"
        meta="Departments, aisles, sections, shelves and bins in each location."
        actions={(
          <>
            <button type="button" className="ci-btn ci-btn-ghost" onClick={exportCsv} disabled={!rows.length}><Download size={15} />Export</button>
            <button type="button" className="ci-btn ci-btn-ghost" onClick={() => fileRef.current?.click()}><Upload size={15} />Import</button>
            <input ref={fileRef} type="file" accept=".csv" hidden onChange={importCsv} />
            {rows.length > 0 && <button type="button" className="ci-btn ci-btn-ghost" onClick={() => setShowGuide((v) => !v)} aria-expanded={showGuide}><CircleHelp size={15} />How it works</button>}
            {rows.length > 0 && <button type="button" className="ci-btn ci-btn-ghost" onClick={() => openLabels(null)}><Printer size={15} />Print labels</button>}
            <button type="button" className="ci-btn ci-btn-primary" onClick={() => openNew()}><Plus size={15} />Add area</button>
          </>
        )}
      />

      {error && <ErrorBanner message={error} />}

      {!loading && rows.length === 0 ? (
        <StorageGuide onStart={() => openNew('Department')} />
      ) : (
      <>
      {showGuide && <StorageGuide onClose={() => setShowGuide(false)} />}

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search areas">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, code, path or barcode…" />
        </label>
        {!searching && tops.length > 0 && (
          <div className="row-actions">
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setExpanded(new Set(tops.map((r) => r.top)))}>Expand all</button>
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setExpanded(new Set())}>Collapse all</button>
          </div>
        )}
      </div>

      {locations?.length > 1 && (
        <FilterChips
          label="Location"
          options={[{ id: 'all', label: 'All locations', count: rows.length }, ...locations.map((l) => ({ id: String(l.location_id), label: l.name, count: rows.filter((r) => String(r.location_id) === String(l.location_id)).length }))]}
          value={locFilter}
          onChange={setLocFilter}
        />
      )}

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table fit-sm">
            <thead>
              <tr>
                <th>Area</th>
                {locations?.length > 1 && <th className="hide-sm">Location</th>}
                <th className="hide-sm">Path</th>
                <th className="hide-sm">Barcode</th>
                <th className="center">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading areas…" /></td></tr>
              ) : tree.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">No areas match.</div></td></tr>
              ) : shownRows.map((r) => {
                const holds = r.depth === 0 ? inside.get(r.top) : null
                const open = searching || expanded.has(r.top)
                return (
                <tr key={r.area_id} className={r.depth === 0 ? 'inv-area-top' : undefined}>
                  <td>
                    {holds ? (
                      <button type="button" className="inv-area-toggle" onClick={() => toggle(r.top)} aria-expanded={open} disabled={searching}>
                        {open ? <ChevronDown size={15} aria-hidden="true" /> : <ChevronRight size={15} aria-hidden="true" />}
                        <span className="inv-area-name">
                          <span className="ci-strong">{r.name || r.code}</span>
                          <span className="cell-sub">{r.level}{r.name ? ` · ${r.code}` : ''} · {holds.bins} {holds.bins === 1 ? 'bin' : 'bins'}{open ? '' : `, ${holds.areas} ${holds.areas === 1 ? 'area' : 'areas'} inside`}</span>
                        </span>
                      </button>
                    ) : (
                      <span className="inv-area-name" style={{ paddingLeft: `${r.depth * 18 + (r.depth === 0 ? 0 : 21)}px` }}>
                        <span className="ci-strong">{r.name || r.code}</span>
                        <span className="cell-sub">{r.level}{r.name ? ` · ${r.code}` : ''}</span>
                      </span>
                    )}
                  </td>
                  {locations?.length > 1 && <td className="hide-sm">{r.location_name || locName(r.location_id)}</td>}
                  <td className="hide-sm ci-muted-cell">{r.path}</td>
                  <td className="hide-sm ci-muted-cell">{r.barcode || '–'}</td>
                  <td className="center">{isActive(r) ? <StatusBadge label="In use" tone="good" /> : <StatusBadge label="Not in use" tone="muted" />}</td>
                  <td className="num">
                    <div className="row-actions">
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => openLabels(r)} title={`Labels for ${r.path}${r.level === 'Bin' ? '' : ' and everything in it'}`}><Printer size={13} />Labels</button>
                      <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => openEdit(r)}>Edit</button>
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{tree.length} {tree.length === 1 ? 'area' : 'areas'} · {tree.filter((r) => r.level === 'Bin').length} bins</span>
        </div>
      </div>
      </>
      )}

      <Modal
        open={Boolean(form)}
        onClose={closeForm}
        title={form?.area_id ? `Edit ${String(form.level).toLowerCase()}` : `Add ${form?.level === 'Aisle' ? 'an' : 'a'} ${String(form?.level || 'area').toLowerCase()}`}
        subtitle={path ? `Path: ${path}` : undefined}
        footer={(
          <>
            <button type="button" className="ci-btn ci-btn-ghost" onClick={closeForm}>{chain ? 'Done' : 'Cancel'}</button>
            <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
          </>
        )}
      >
        {form && (
          <div className="inv-area-form">
            {chain && <p className="inv-chain" role="status">{chain}</p>}
            {formError && <ErrorBanner message={formError} />}
            <label className="ci-form-field">
              <span>Location</span>
              <select value={form.location_id} onChange={(e) => set({ location_id: e.target.value, parent_id: '' })}>
                <option value="">Choose…</option>
                {(locations || []).map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </label>
            <label className="ci-form-field">
              <span>What is it?</span>
              <select value={form.level} onChange={(e) => set({ level: e.target.value, parent_id: '', code: '' })} disabled={Boolean(form.area_id)}>
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <small className="inv-field-help">{LEVEL_HELP[form.level].what}</small>
            </label>
            {PARENT_LEVEL[form.level] && (
              <label className="ci-form-field">
                <span>Inside which {PARENT_LEVEL[form.level].toLowerCase()}?</span>
                <select value={form.parent_id} onChange={(e) => set({ parent_id: e.target.value })} disabled={!parentOptions.length}>
                  <option value="">{parentOptions.length ? 'Choose…' : `None at this location yet`}</option>
                  {parentOptions.map((o) => <option key={o.area_id} value={o.area_id}>{o.name ? `${o.name} (${o.path})` : o.path}</option>)}
                </select>
                {!parentOptions.length && (
                  <small className="inv-field-help">
                    {form.level === 'Aisle' ? 'An' : 'A'} {form.level.toLowerCase()} goes inside {PARENT_LEVEL[form.level] === 'Aisle' ? 'an' : 'a'} {PARENT_LEVEL[form.level].toLowerCase()}.{' '}
                    <button type="button" className="link-button" onClick={() => set({ level: PARENT_LEVEL[form.level], parent_id: '', code: '' })}>Add {PARENT_LEVEL[form.level] === 'Aisle' ? 'an' : 'a'} {PARENT_LEVEL[form.level].toLowerCase()} first</button>
                  </small>
                )}
              </label>
            )}
            <label className="ci-form-field">
              <span>Code</span>
              <input value={form.code} onChange={(e) => set({ code: e.target.value })} placeholder={`e.g. ${LEVEL_HELP[form.level].eg.split(',')[0]}`} />
              <small className="inv-field-help">Short and unique on its {PARENT_LEVEL[form.level] ? PARENT_LEVEL[form.level].toLowerCase() : 'location'}. It becomes part of the path, e.g. {LEVEL_HELP[form.level].eg}.</small>
            </label>
            <label className="ci-form-field">
              <span>Name (optional)</span>
              <input value={form.name || ''} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Dry goods" />
            </label>
            <label className="ci-form-field">
              <span>Barcode (optional)</span>
              <div className="scan-field">
                <input value={form.barcode || ''} onChange={(e) => set({ barcode: e.target.value })} placeholder="Scan the label on the shelf" aria-label="Barcode (optional)" />
                <ScanButton onScan={(code) => set({ barcode: code })} />
              </div>
            </label>
            <label className="ci-form-toggle">
              <input type="checkbox" checked={form.is_active === '1'} onChange={(e) => set({ is_active: e.target.checked ? '1' : '0' })} />
              In use
            </label>
          </div>
        )}
      </Modal>
      <Modal
        open={Boolean(labels)}
        onClose={() => setLabels(null)}
        size="lg"
        title="Print labels"
        subtitle={labelRoot ? `${labelRoot.path} and everything in it` : (locFilter === 'all' ? 'Every location' : locName(locFilter))}
        footer={(
          <>
            <button type="button" className="ci-btn ci-btn-ghost" onClick={() => setLabels(null)}>Cancel</button>
            <button type="button" className="ci-btn ci-btn-primary" disabled={!labelList.length} onClick={printHere}><Printer size={14} />Print {labelList.length} {labelList.length === 1 ? 'label' : 'labels'}</button>
          </>
        )}
      >
        {labels && (
          <div className="inv-area-form">
            <div className="ci-form-field">
              <span>Labels for</span>
              <div className="lbl-levels">
                {LEVELS.map((l) => (
                  <label key={l} className="ci-form-toggle">
                    <input type="checkbox" checked={labels.levels.includes(l)} onChange={() => toggleLevel(l)} />
                    {l === 'Shelf' ? 'Shelves' : `${l}s`}
                  </label>
                ))}
              </div>
            </div>
            <div className="segmented" role="radiogroup" aria-label="Label size">
              <button type="button" role="radio" aria-checked={labels.size === 'small'} className={labels.size === 'small' ? 'active' : ''} onClick={() => setLabels((l) => ({ ...l, size: 'small' }))}>
                Small<small>3 across</small>
              </button>
              <button type="button" role="radio" aria-checked={labels.size === 'large'} className={labels.size === 'large' ? 'active' : ''} onClick={() => setLabels((l) => ({ ...l, size: 'large' }))}>
                Large<small>2 across, bigger barcode</small>
              </button>
            </div>
            {labelList.length ? (
              <>
                <p className="doc-hint">{labelList.length} {labelList.length === 1 ? 'label' : 'labels'}. The first few:</p>
                <div className={`lbl-preview lbl-sheet-${labels.size}`}>
                  {labelList.slice(0, 4).map((a) => <Label key={a.area_id} area={a} size={labels.size} multiLocation={locations?.length > 1} />)}
                </div>
                <p className="doc-hint">Print on plain paper and cut, or on a sheet of sticky labels. Each barcode holds the {labelList.some((a) => a.barcode) ? 'area’s own barcode, or else its ' : 'area’s '}{locations?.length > 1 ? 'location and path' : 'path'}. Reprint a label after renaming its area.</p>
              </>
            ) : <p className="doc-hint">Nothing to print. Tick at least one kind of area.</p>}
          </div>
        )}
      </Modal>

      {printing && <LabelSheet areas={printing.list} size={printing.size} multiLocation={locations?.length > 1} />}
    </div>
  )
}

// Plain instructions for building storage areas, shown in place of the
// empty list and behind "How it works" once areas exist.
function StorageGuide({ onStart, onClose }) {
  return (
    <section className="doc-card inv-guide">
      <div className="doc-card-head">
        <h2>How to set up storage areas</h2>
        {onClose && <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={onClose}>Hide</button>}
      </div>
      <div className="inv-guide-body">
        <p>Storage areas tell everyone exactly where each item is kept. Build them from the biggest space down to the smallest. There are five levels, and each sits inside the one before it:</p>
        <ol className="inv-guide-levels">
          {LEVELS.map((l) => (
            <li key={l}><strong>{l}</strong>: {LEVEL_HELP[l].what} <span className="sum-muted">For example {LEVEL_HELP[l].eg}.</span></li>
          ))}
        </ol>
        <p>The codes join into each bin’s path. <code>/DRY/A1/S2/SH3/B4</code> is Dry goods, aisle 1, section 2, shelf 3, bin 4. Print it on the bin’s label.</p>
        <h3>Steps</h3>
        <ol>
          <li>Press <strong>Add area</strong>, choose <strong>Department</strong>, give it a short code and save.</li>
          <li>The form then moves on to an aisle inside it, then a section, then a shelf. Check each code and save.</li>
          <li>On the shelf, add its bins. After each bin the next code is filled in for you (B1, B2, B3…).</li>
          <li>Press <strong>Done</strong>, then use <strong>Add area</strong> again for the next aisle or shelf.</li>
        </ol>
        <h3>Tips</h3>
        <ul>
          <li>Press <strong>Print labels</strong> (or <strong>Labels</strong> on a row) to print a barcode label for each shelf and bin. Stick them on, and staff can scan a bin instead of choosing it from a list.</li>
          <li>Setting up many bins? Press <strong>Export</strong> to see the columns, fill them in a spreadsheet, then <strong>Import</strong> it.</li>
          <li>Once you have bins, put received goods into them under <strong>Put away &amp; move</strong>.</li>
        </ul>
        {onStart && (
          <div>
            <button type="button" className="ci-btn ci-btn-primary" onClick={onStart}><Plus size={14} />Add your first department</button>
          </div>
        )}
      </div>
    </section>
  )
}
